import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import api from '../services/api'
import { Memory } from '../types'

export default function DashboardPage() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [memories, setMemories] = useState<Memory[]>([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    total: 0,
    byCategory: {} as Record<string, number>,
    bySource: {} as Record<string, number>,
  })

  useEffect(() => {
    loadMemories()
  }, [])

  const loadMemories = async () => {
    try {
      const data = await api.getMemories({ limit: 100 })
      setMemories(data)
      
      // Calculate stats
      const byCategory: Record<string, number> = {}
      const bySource: Record<string, number> = {}
      
      data.forEach((memory) => {
        byCategory[memory.category] = (byCategory[memory.category] || 0) + 1
        bySource[memory.source] = (bySource[memory.source] || 0) + 1
      })
      
      setStats({
        total: data.length,
        byCategory,
        bySource,
      })
    } catch (error) {
      console.error('Failed to load memories:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Business VDB Service
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Welcome back, {user?.full_name || user?.username}!
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/memories')}>
              Memories
            </Button>
            <Button variant="outline" onClick={() => navigate('/categories')}>
              Categories
            </Button>
            <Button variant="outline" onClick={() => navigate('/organizations')}>
              Organizations
            </Button>
            <Button variant="outline" onClick={() => navigate('/connectors')}>
              Connectors
            </Button>
            <Button variant="outline" onClick={() => navigate('/profile')}>
              Profile
            </Button>
            <Button variant="outline" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Total Memories Card */}
          <Card>
            <CardHeader>
              <CardTitle>Total Memories</CardTitle>
              <CardDescription>All stored memories</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-primary">
                {loading ? '...' : stats.total}
              </div>
            </CardContent>
          </Card>

          {/* By Category Card */}
          <Card>
            <CardHeader>
              <CardTitle>By Category</CardTitle>
              <CardDescription>Memory distribution</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div>Loading...</div>
              ) : Object.keys(stats.byCategory).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(stats.byCategory).map(([category, count]) => (
                    <div key={category} className="flex justify-between items-center">
                      <span className="text-sm font-medium capitalize">{category}</span>
                      <span className="text-sm text-muted-foreground">{count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">No memories yet</div>
              )}
            </CardContent>
          </Card>

          {/* By Source Card */}
          <Card>
            <CardHeader>
              <CardTitle>By Source</CardTitle>
              <CardDescription>Memory sources</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div>Loading...</div>
              ) : Object.keys(stats.bySource).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(stats.bySource).map(([source, count]) => (
                    <div key={source} className="flex justify-between items-center">
                      <span className="text-sm font-medium capitalize">{source}</span>
                      <span className="text-sm text-muted-foreground">{count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">No memories yet</div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Memories */}
        <Card className="mt-6">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Recent Memories</CardTitle>
                <CardDescription>Your latest stored memories</CardDescription>
              </div>
              <Button onClick={() => navigate('/memories')}>View All</Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : memories.length > 0 ? (
              <div className="space-y-4">
                {memories.slice(0, 5).map((memory) => (
                  <div
                    key={memory.id}
                    className="p-4 border rounded-lg hover:bg-accent cursor-pointer transition-colors"
                    onClick={() => navigate('/memories')}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex gap-2">
                        <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded">
                          {memory.category}
                        </span>
                        <span className="text-xs px-2 py-1 bg-secondary text-secondary-foreground rounded">
                          {memory.source}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(memory.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm line-clamp-2">{memory.content}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>No memories yet. Create your first memory!</p>
                <Button className="mt-4" onClick={() => navigate('/memories')}>
                  Create Memory
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  )
}
